import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { allPaymentTypes, enumPaymentTypes, DISCOUNT, BALANCE, DEPOSIT1 } from '../../consts/paymentTypes';
import { formatPrice } from '../../helpers/common';
import { getFirstNonMatchingItem } from '../../helpers/sidebar';
import { getExistingPaymentTypes, getOwedForType } from './paymentHelpers';
import { enumStatuses, OPEN } from '../../consts/status';

const buildRows = (selectedPOs) => {
    const today = new Date().toISOString().split('T')[0];
    return selectedPOs.map((po) => {
        const hasPayments = Array.isArray(po.payments) && po.payments.length > 0;
        const suggestedType = hasPayments
            ? (getFirstNonMatchingItem(allPaymentTypes, po.payments) || BALANCE)
            : DEPOSIT1;
        return {
            poId: po.id,
            included: true,
            amount: '',
            type: suggestedType,
            date: today,
            transactionId: '',
            note: ''
        };
    });
};

export const BulkPaymentDialog = ({
    open,
    selectedPOs,
    onSubmit,
    onClose,
    testId
}) => {
    const [rows, setRows] = useState([]);
    const [warnings, setWarnings] = useState(null);

    useEffect(() => {
        if (open) {
            setRows(buildRows(selectedPOs.filter((po) => po.status === OPEN)));
            setWarnings(null);
        }
    }, [open, selectedPOs]);

    const poById = useMemo(
        () => Object.fromEntries(selectedPOs.map((po) => [po.id, po])),
        [selectedPOs]
    );
    const blockedPOs = selectedPOs.filter((po) => po.status !== OPEN);
    const blockedMessage = blockedPOs.length > 0
        ? `${blockedPOs.map((po) => `${po.poNumber || po.id} (${enumStatuses[po.status] || po.status || 'Unknown'})`).join(', ')} can't take payments — set them to Open first.`
        : '';

    if (!open) return null;

    const includedRows = rows.filter((row) => row.included);
    const openRows = includedRows.filter((row) => poById[row.poId]?.status === OPEN);
    const total = includedRows.reduce((acc, row) => acc + (parseFloat(row.amount) || 0), 0);

    const updateRow = (poId, changes) =>
        setRows((prev) => prev.map((row) => (row.poId === poId ? { ...row, ...changes } : row)));

    const validateRows = () => {
        if (openRows.length === 0) {
            toast.error(blockedMessage || 'Please set this PO to Open to add a payment.', { position: 'bottom-right' });
            return null;
        }
        for (const row of openRows) {
            const amount = parseFloat(row.amount);
            if (!row.amount || Number.isNaN(amount)) {
                toast.error(`Enter a valid amount for ${poById[row.poId]?.poNumber || row.poId}`, { position: 'bottom-right' });
                return null;
            }
            if (row.type === DISCOUNT && amount >= 0) {
                toast.error(`Discount amount must be less than 0 for ${poById[row.poId]?.poNumber || row.poId}`, { position: 'bottom-right' });
                return null;
            }
        }
        return openRows;
    };

    const submitOpenRows = () => {
        if (openRows.length === 0) {
            toast.error(blockedMessage || 'Please set this PO to Open to add a payment.', { position: 'bottom-right' });
            return;
        }
        onSubmit(openRows);
    };

    const handleSubmitClick = () => {
        const validRows = validateRows();
        if (!validRows) return;
        const messages = [];
        for (const row of validRows) {
            const po = poById[row.poId];
            if (!po) continue;
            const amount = parseFloat(row.amount);
            if (getExistingPaymentTypes(po.payments).has(row.type)) {
                messages.push(`${po.poNumber} already has a ${enumPaymentTypes[row.type]} payment recorded.`);
            }
            const owed = getOwedForType(po, row.type);
            if (row.type !== DISCOUNT && amount > owed + 0.005) {
                messages.push(`${po.poNumber}: payment of $${formatPrice(amount)} exceeds the $${formatPrice(owed)} owed for ${enumPaymentTypes[row.type]}.`);
            }
        }
        if (messages.length > 0) {
            setWarnings(messages);
            return;
        }
        onSubmit(validRows);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center overflow-y-auto p-4" data-testid={testId}>
            <div className="fixed inset-0 bg-gray-700 opacity-40" onClick={onClose} />
            <div className="relative w-full max-w-5xl bg-white rounded-lg shadow-xl z-[61] max-h-[90vh] flex flex-col">
                {warnings ? (
                    <div className="p-5">
                        <h3 className="text-lg font-bold mb-3">Please confirm these payments</h3>
                        <div className="space-y-2 text-sm text-gray-700 max-h-60 overflow-y-auto">
                            {warnings.map((message, index) => (
                                <p key={index}>{message}</p>
                            ))}
                            <p>Do you wish to proceed?</p>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                onClick={() => setWarnings(null)}
                                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                            >
                                No, go back
                            </button>
                            <button
                                onClick={submitOpenRows}
                                className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-gray-800"
                            >
                                Yes, proceed
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex items-start justify-between p-5 pb-2 shrink-0">
                            <div>
                                <h3 className="text-lg font-bold">Bulk Add Payment — {selectedPOs.length} POs</h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Each PO gets its own payment record — amount, type, date, transaction # and note are unique per PO.
                                </p>
                            </div>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                        {blockedMessage && (
                            <div className="mx-5 mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                {blockedMessage}
                            </div>
                        )}
                        {/* Mobile cards */}
                        <div className="md:hidden flex-1 min-h-0 overflow-y-auto px-5 pb-3 space-y-3">
                            {rows.map((row) => {
                                const po = poById[row.poId];
                                if (!po) return null;
                                return (
                                    <div key={row.poId} className={`rounded-lg border border-gray-200 p-3 transition-opacity ${row.included ? '' : 'opacity-40'}`}>
                                        <div className="flex items-center gap-2.5">
                                            <input
                                                type="checkbox"
                                                checked={row.included}
                                                onChange={(e) => updateRow(row.poId, { included: e.target.checked })}
                                                className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                                                aria-label={`Include ${po.poNumber}`}
                                            />
                                            <span className="font-bold text-[15px] whitespace-nowrap">{po.poNumber}</span>
                                            <span className="text-xs text-gray-500 truncate">{po.vendor}</span>
                                        </div>
                                        <div className="mt-1.5 pl-[30px] text-[13px]">
                                            Owed for {enumPaymentTypes[row.type]}:{' '}
                                            <span className="font-semibold text-red-600">${formatPrice(getOwedForType(po, row.type))}</span>
                                        </div>
                                        <div className="mt-2.5 pl-[30px] grid grid-cols-2 gap-2">
                                            <div className="space-y-1">
                                                <div className="text-[11px] font-medium text-gray-500">Type</div>
                                                <select
                                                    value={row.type}
                                                    disabled={!row.included}
                                                    onChange={(e) => updateRow(row.poId, { type: e.target.value })}
                                                    className="w-full text-sm border-gray-300 rounded-md"
                                                >
                                                    {allPaymentTypes.map((option) => (
                                                        <option key={option} value={option}>
                                                            {enumPaymentTypes[option]}
                                                            {getExistingPaymentTypes(po.payments).has(option) ? ' (paid)' : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-[11px] font-medium text-gray-500">Date</div>
                                                <input
                                                    type="date"
                                                    value={row.date}
                                                    disabled={!row.included}
                                                    onChange={(e) => updateRow(row.poId, { date: e.target.value })}
                                                    className="w-full text-sm border-gray-300 rounded-md"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-[11px] font-medium text-gray-500">Amount</div>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={row.amount}
                                                    disabled={!row.included}
                                                    onChange={(e) => updateRow(row.poId, { amount: e.target.value.replace(/[^0-9.-]/g, '') })}
                                                    placeholder="0.00"
                                                    className="no-arrows-by-input w-full text-sm text-right border-gray-300 rounded-md"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-[11px] font-medium text-gray-500">Transaction #</div>
                                                <input
                                                    type="text"
                                                    value={row.transactionId}
                                                    disabled={!row.included}
                                                    onChange={(e) => updateRow(row.poId, { transactionId: e.target.value })}
                                                    placeholder="e.g. P26072401"
                                                    className="w-full text-sm border-gray-300 rounded-md"
                                                />
                                            </div>
                                            <div className="col-span-2 space-y-1">
                                                <div className="text-[11px] font-medium text-gray-500">Note</div>
                                                <textarea
                                                    value={row.note}
                                                    rows={2}
                                                    disabled={!row.included}
                                                    onChange={(e) => updateRow(row.poId, { note: e.target.value })}
                                                    placeholder="e.g. Bill"
                                                    className="w-full text-sm border-gray-300 rounded-md resize-y"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Desktop table */}
                        <div className="hidden md:block flex-1 min-h-0 overflow-y-auto px-5">
                            <table className="w-full text-xs">
                                <thead className="sticky top-0 bg-white z-10">
                                    <tr className="text-left text-gray-500">
                                        <th className="py-1.5 w-7"></th>
                                        <th className="py-1.5 pr-3 font-medium">PO</th>
                                        <th className="py-1.5 pr-3 font-medium">Vendor</th>
                                        <th className="py-1.5 pr-4 font-medium text-right">Owed</th>
                                        <th className="py-1.5 pr-3 font-medium">Type</th>
                                        <th className="py-1.5 pr-3 font-medium">Date</th>
                                        <th className="py-1.5 pr-3 font-medium text-right">Amount</th>
                                        <th className="py-1.5 pr-3 font-medium">Transaction #</th>
                                        <th className="py-1.5 font-medium">Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const po = poById[row.poId];
                                        if (!po) return null;
                                        return (
                                            <tr key={row.poId} className={`transition-opacity ${row.included ? '' : 'opacity-40'}`}>
                                                <td className="py-1.5 pr-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={row.included}
                                                        onChange={(e) => updateRow(row.poId, { included: e.target.checked })}
                                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                        aria-label={`Include ${po.poNumber}`}
                                                    />
                                                </td>
                                                <td className="py-1.5 pr-3 font-semibold whitespace-nowrap">{po.poNumber}</td>
                                                <td className="py-1.5 pr-3 text-gray-500 truncate max-w-[140px]">{po.vendor}</td>
                                                <td className="py-1.5 pr-4 text-right text-red-600 font-semibold whitespace-nowrap" title={`Total PO owed: $${formatPrice(po.amountOwed)}`}>
                                                    ${formatPrice(getOwedForType(po, row.type))}
                                                </td>
                                                <td className="py-1.5 pr-3">
                                                    <select
                                                        value={row.type}
                                                        disabled={!row.included}
                                                        onChange={(e) => updateRow(row.poId, { type: e.target.value })}
                                                        className="w-full text-xs border-gray-300 rounded-md min-w-24"
                                                    >
                                                        {allPaymentTypes.map((option) => (
                                                            <option key={option} value={option}>
                                                                {enumPaymentTypes[option]}
                                                                {getExistingPaymentTypes(po.payments).has(option) ? ' (paid)' : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="py-1.5 pr-3">
                                                    <input
                                                        type="date"
                                                        value={row.date}
                                                        disabled={!row.included}
                                                        onChange={(e) => updateRow(row.poId, { date: e.target.value })}
                                                        className="w-full text-xs border-gray-300 rounded-md"
                                                    />
                                                </td>
                                                <td className="py-1.5 pr-3">
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={row.amount}
                                                        disabled={!row.included}
                                                        onChange={(e) => updateRow(row.poId, { amount: e.target.value.replace(/[^0-9.-]/g, '') })}
                                                        placeholder="0.00"
                                                        className="no-arrows-by-input w-24 text-xs text-right border-gray-300 rounded-md"
                                                    />
                                                </td>
                                                <td className="py-1.5 pr-3">
                                                    <input
                                                        type="text"
                                                        value={row.transactionId}
                                                        disabled={!row.included}
                                                        onChange={(e) => updateRow(row.poId, { transactionId: e.target.value })}
                                                        placeholder="e.g. P26072401"
                                                        className="w-full text-xs border-gray-300 rounded-md"
                                                    />
                                                </td>
                                                <td className="py-1.5">
                                                    <input
                                                        type="text"
                                                        value={row.note}
                                                        disabled={!row.included}
                                                        onChange={(e) => updateRow(row.poId, { note: e.target.value })}
                                                        placeholder="e.g. Bill"
                                                        className="w-full text-xs border-gray-300 rounded-md"
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex items-center justify-between gap-2 p-5 pt-3 border-t border-gray-100 shrink-0">
                            <span className="text-sm text-gray-500">
                                {includedRows.length} of {rows.length} POs included · Total{' '}
                                <span className="font-semibold text-gray-900">${formatPrice(total)}</span>
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmitClick}
                                    className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-gray-800"
                                >
                                    Record {includedRows.length} Payment{includedRows.length === 1 ? '' : 's'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
